import { getCMSSettings, getCMSSlot } from '@tools/cmsBlockSettings';
import { makeStyles } from '@material-ui/styles';
import theme from '../../../theme';
import { Grid } from '@material-ui/core';
import { useEffect, useMemo, useRef, useState } from 'react';
import { elevateClickNotification, elevateGeneralRecommendations, elevateLandingPage } from '@tools/elevateClient';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import ElevateListing from '../../../page/search/elevateListing';
import { useRouter } from 'next/router';
import {
    getDeliveryTimeId,
    getFastDelivery,
    getMainVariant,
    getMarketingLabel,
    getProductVariants,
    getStock,
    getUUID,
    prepareFacets,
    preventFloatRoundingIssues,
} from '@functions/elevateOps';
import SlickWrapper from '../../../slick/productSlider';
import ProductItem from '../../../products/item';
import urlNormalizer from '../../../../tools/urlNormalization';
import ProductThumbnail from '@components/products/thumbnail';
import StorefrontApiService from '@services/StorefrontApiService';

const useStyles = makeStyles(
    {
        wrapper: {
            paddingTop: 0,
            paddingBottom: 0,
            width: '100%',
            margin: 'auto',
        },
    },
    { name: 'VoyadoProductBlock' },
);

export default function VoyadoProductBlock({ block, slots, breadcrumbs, category }) {
    const router = useRouter();
    const { query } = router;
    const [ params, setParams ] = useState({ ...query, initial: true });
    const classes = useStyles();
    const CMSSettings = getCMSSettings(block, 0);
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [ elevateData, setElevateData ] = useState({
        facets: [],
        productGroups: [],
        sort: { selected: 'RELEVANCE', options: [] },
        totalHits: 0,
    });
    const productBlock = getCMSSlot('voyadoProductBlock', slots);
    const type = productBlock?.translated?.config?.type.value;
    const recommendationId = productBlock?.translated?.config?.recommendationId?.value || false;
    const selectedImageType = productBlock?.translated?.config?.imageType?.value || 'standard';
    const limit = productBlock?.translated?.config?.limit?.value || 12;
    const blockRef = useRef(null);
    const [products, setProducts] = useState([]);

    useEffect(async () => {
        if (type === 'landing-page') {
            getVoyadoLandingPage();
        } else if (type === 'recommendations') {
            getVoyadoRecommendations();
        }
    }, [ router ]);

    useEffect(() => {
        setParams({ ...query, initial: true });
    }, [ query ]);

    useEffect(async () => {
        let routerData = {
            pathname: router.asPath.split('?')[0],
            query: {
                ...params,
            },
            shallow: true,
            scroll: false,
        };

        if (routerData.query.page === 1) {
            delete routerData.query.page;
        }

        if (routerData.query.slug) {
            delete routerData.query.slug;
        }

        delete routerData.query.initial;

        if (!params.initial) {
            router.push(routerData, undefined, { shallow: true, scroll: false });
            if (blockRef?.current) {
                window.scrollTo({
                    top: parseInt(blockRef.current?.offsetTop - document.querySelector('.header-lower-inner.desktop')?.getBoundingClientRect().bottom),
                    behavior: 'smooth',
                });
            }
        }
    }, [ params ]);

    async function onClick(product) {
        await elevateClickNotification(
            product.ticket,
            isMobile ? 'mobile' : 'desktop',
        );
    }

    useMemo(() => {
        let products = {};

        elevateData?.productGroups?.map((productSuggestions) => {
            const p = productSuggestions.products[0];
            console.log(p);
            products[p.variants[0]?.custom?.uuid[0]?.id] = p;
        });

        const ids = Object.keys(products);

        if (ids.length > 0) {
            const api = new StorefrontApiService();
            api.getProductsElevated(ids).then((response) => {
                response.products.forEach((product) => {
                    products[product.id].streamIds = product.streamIds;
                });
                setProducts(Object.values(products));
            })
        }

        setProducts(Object.values(products));
    }, [ elevateData ]);

    async function getVoyadoLandingPage() {
        let { q, page, order, slug, initial, ...filters } = router.query;

        const result = await elevateLandingPage(
            productBlock?.translated?.config?.pageUrl?.value,
            order || 'RELEVANCE',
            prepareFacets(filters),
            page,
            isMobile ? 'mobile' : 'desktop',
            limit,
        );

        let primaryList = {};
        let recommendationList = {};
        result.primaryList ? primaryList = result.primaryList : {};

        if (result.recommendationLists) {
            recommendationList = result.recommendationLists[0];
            if (recommendationId) {
                const tempList = result.recommendationLists.find(list => list.id === recommendationId);
                if (tempList) {
                    recommendationList = tempList;
                }
            }
        }
        setElevateData({ ...elevateData, ...recommendationList, ...primaryList });
    }

    async function getVoyadoRecommendations() {
        const result = await elevateGeneralRecommendations(
            productBlock?.translated?.config?.algorithm.value,
            limit,
            isMobile ? 'mobile' : 'desktop');

        let recommendationList = {};
        result.recommendationLists ? recommendationList = result?.recommendationLists[0] : {};
        setElevateData({ ...elevateData, ...recommendationList });
    }

    function isSlider() {
        return !!productBlock?.config?.slider?.value;
    }

    function renderProducts(sizes) {
        return products.length > 0 ? products.map((product, key) => {
            const mainVariant = getMainVariant(product);
            const labels = getMarketingLabel(mainVariant);

            return <ProductItem
                id={getUUID(mainVariant)}
                productNumber={mainVariant.key}
                propertyIds={[ ...labels ]}
                name={product.title}
                manufacturer={product.brand}
                link={urlNormalizer(product.link, router.basePath)}
                prices={{
                    price: mainVariant.sellingPrice,
                    listPrice: mainVariant.listPrice,
                    isOnSale: mainVariant.sellingPrice < mainVariant.listPrice,
                    discount: mainVariant.sellingPrice < mainVariant.listPrice ? {
                        amount: mainVariant.sellingPrice - mainVariant.listPrice,
                        percentage: preventFloatRoundingIssues(mainVariant.sellingPrice, mainVariant.listPrice),
                    } : undefined,
                }}
                image={<ProductThumbnail
                    imageType={selectedImageType}
                    alt={mainVariant.name}
                    product={{ id: getUUID(mainVariant) }}
                />}
                list="Search results"
                variants={getProductVariants(product) ? mainVariant.label : false}
                stock={getStock(mainVariant)}
                fastDelivery={getFastDelivery(mainVariant)}
                available={mainVariant.inStock}
                addToCart={'minimal'}
                isOutlet={product.title.indexOf('OUTLET') >= 0}
                position={key + 1}
                clickCallback={onClick.bind(this, product)}
                elevateTicket={mainVariant.ticket}
                isMobile={isMobile}
                customClasses={{ item: classes.productItem }}
                lazyHydrate={false}
                sizes={sizes}
                streamIds={product.streamIds || []}
                deliveryTimeString={getDeliveryTimeId(product)}
                key={key}
            />;
        }) : '';
    }

    return (
        <Grid
            ref={blockRef}
            container
            direction="row"
            justify="center"
            alignItems="stretch"
            spacing={1}
            className={classes.wrapper}
            style={{
                ...CMSSettings.styles,
            }}
        >
            {type === 'landing-page' &&
                (isSlider() ? (
                    <Grid xs={12} item>
                        <SlickWrapper center={true} showAdditional={false}>
                            {renderProducts({ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 })}
                        </SlickWrapper>
                    </Grid>
                ) : (
                    <div className={classes.wrapper}>
                        <ElevateListing
                            imageType={selectedImageType}
                            router={router}
                            params={params}
                            setParams={setParams}
                            {...elevateData}
                        />
                    </div>
                ))}

            {type === 'recommendations' &&
                (isSlider() ? (
                    <Grid xs={12} item>
                        <SlickWrapper center={true} showAdditional={false}>
                            {renderProducts({ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 })}
                        </SlickWrapper>
                    </Grid>
                ) : (
                    renderProducts()
                ))}
        </Grid>
    );
}
